import { computed, onMounted, onUnmounted, Ref, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import { storeToRefs } from 'pinia';

import api from '@/api';
import { nextChatId } from '@/api/apiService';
import { useHangUpCommon } from '@/composables/domains/chat/useHangupCommon';
import { useSocketCall } from '@/composables/domains/chat/useSocketCall';
import { useAccount } from '@/composables/shared/useAccount';
import { useNotification } from '@/composables/shared/useNotification';
import { useAudioStore } from '@/stores/audio';
import { useCallStore } from '@/stores/call';
import { QRCodeType } from '@/stores/chat';
import { useMicrophoneStore } from '@/stores/microphone';
import { SocketState, useSocketStore } from '@/stores/socket';
import { useToastStore } from '@/stores/toast.js';
import { UserType, useUserStore } from '@/stores/user';
import { LanguageCode, TypeCall, TypeDomain } from '@/types/common';
import { BottomBarStatus } from '@/types/components';
import { ConnectionInfo, SocketCloseReasonCode } from '@/types/socket';
import redirectToLoginPage from '@/utils/redirectToLoginPage';

export const useSocketController = (code: Ref<string>, callType: Ref<TypeCall>, domain: TypeDomain) => {
  const audioStore = useAudioStore();
  const callStore = useCallStore();
  const socketStore = useSocketStore();
  const toastStore = useToastStore();
  const userStore = useUserStore();
  const { userInfo } = storeToRefs(userStore);
  const { selectedChatId } = storeToRefs(socketStore);
  const microphoneStore = useMicrophoneStore();

  const { t } = useI18n();
  const router = useRouter();

  /* states */
  const bottomBarStatus = ref<BottomBarStatus>(0); // -2:recover / -1:failed / 0:on / 1:connecting / 2:end
  const waitingToRetry = ref<boolean>(false);
  const retryCount = ref<number>(0);
  const loading = ref<boolean>(socketStore.socketState !== 'OPEN');
  const CALL_TYPES_QR = ['GENERAL', 'CONFERENCE', 'MEETING'] as const satisfies readonly TypeCall[];

  const { alertExceededUsageLimit, alertEvictMyself, alertNoEmployee, alertAlreadyEndedCallWithReconnect, alertBasic } =
    useNotification();
  const { logoutAndGoToLogin } = useAccount();

  const setBottomBarStatus = (status: BottomBarStatus) => {
    bottomBarStatus.value = status;
  };

  const { connectToWebsocket } = useSocketCall(setBottomBarStatus);
  const { handleHangUp } = useHangUpCommon({ setBottomBarStatus });

  /* getters */
  const connectionInfo = computed<Partial<ConnectionInfo>>(() => {
    return (
      {
        GENERAL: {
          chatId: selectedChatId.value,
        },
        CONFERENCE: {
          callType: 'CONFERENCE',
          chatId: selectedChatId.value,
        },
        MEETING: {
          chatId: selectedChatId.value,
          callType: 'MEETING',
          isTssOn: '0',
          voiceId: '0',
        },
        AUDIENCE: {
          chatId: userStore.userId,
          callType: 'AUDIENCE',
          isTssOn: '0',
          voiceId: '0',
          evictMySelf: '1',
        },
      }[callType.value] || ({ chatId: selectedChatId.value } as Partial<ConnectionInfo>)
    );
  });

  /* actions */
  /**
   * @desc chatId를 초기화 시킵니다.
   * */
  const initChatId = async () => {
    if (['AUDIENCE'].includes(callType.value) && code.value) {
      selectedChatId.value = code.value;
    } else if (code.value) {
      const { data: nextId = '' } = await nextChatId(code.value);
      selectedChatId.value = nextId;
    }
  };

  /**
   * @desc 언어설정을 초기화 합니다.
   * */
  const initLanguage = () => {
    const defaultLanguage = (audioStore.languageSelected?.code ||
      localStorage.getItem('language') ||
      userStore.pageLanguage) as LanguageCode;

    audioStore.selectLanguage(defaultLanguage);
  };

  /**
   * @desc Auth Token 정보를 가져옵니다.
   * */
  const initAuthToken = async () => {
    const authToken = localStorage.getItem('authToken');
    const { qrCodeType = '' } = callStore.qrCodeInfo || { qrCodeType: '' };

    const deleteAuthToken = async () => {
      await userStore.requestLogout();
      await userStore.fetchUserInfo();
    };

    if (QRCodeType.isGuest(qrCodeType) && authToken) {
      // 01) 게스트 + auth token 보유시
      await deleteAuthToken();
    } else if (!userStore.userInfo) {
      redirectToLoginPage();
    } else if (QRCodeType.isEmployee(qrCodeType) && UserType.isUser(userStore.userInfo.userType)) {
      // 02) 호스트방 접근 여부 & 사용자 게스트 여부
      await deleteAuthToken();
      redirectToLoginPage();
    }
  };

  /**
   * @desc QR 정보
   * */
  const initQrInfo = async () => {
    if (callType.value === 'AUDIENCE') {
      callStore.init();
      callStore.updateCode(code.value);
    } else if (!code.value) {
      return await router.push({ name: 'chat-ended' });
    } else {
      callStore.init();
      callStore.updateCode(code.value);
      const resultQR = await api.get(`/api/qr/${code.value}`);
      callStore.setQrCodeInfo({ ...resultQR.data, code: code.value });
    }
  };

  /**
   * @desc QR 정보
   * */
  const initUserInfo = async () => {
    if (['AUDIENCE'].includes(callType.value)) {
      const { userType } = userInfo.value || {};
      userInfo.value = ['HOST', 'SUB'].includes(userType) ? userInfo.value : null;
    } else {
      await userStore.fetchUserInfo();
    }
  };

  const setupWebsocket = async () => {
    // 01) 언어 정보
    initLanguage();
    // 02) QR 정보
    await initQrInfo();
    // 04) 유저 정보
    await initUserInfo();
    // 03) Auth Token 정보
    await initAuthToken();
    // 05) 통화방 ID
    await initChatId();
  };

  const initProcess = async () => {
    socketStore.exceededUsageLimit = false;
    socketStore.setRemainingMinutes(0);

    if (!socketStore.channel) {
      socketStore.channel = new BroadcastChannel('connect_ws');
    }

    socketStore.channel.onmessage = (event) => {
      if (event.data.type === 'connect_ws') {
        const splited = event.data.value.split('|');
        const selectedChatId = splited[0];
        const selectedQRCodeType = splited[1];
        if (!selectedChatId || !selectedQRCodeType) {
          return;
        }
        if (
          socketStore.selectedChatId !== selectedChatId ||
          (socketStore.selectedChatId === selectedChatId && callStore.qrCodeInfo.qrCodeType !== selectedQRCodeType)
        ) {
          handleHangUp();
          alert(t('alert_end_call_catch_another_access'));
          window.location.href = '/?code=' + callStore.qrCodeInfo.code;
        }
      }
    };

    if (socketStore.socket?.readyState !== WebSocket.OPEN) {
      await connectWebsocket(true);
    } else if (![0, -2].includes(bottomBarStatus.value)) {
      setBottomBarStatus(0);
    }
  };

  const connectWebsocket = async (isForceful: boolean) => {
    await connectToWebsocket(isForceful, connectionInfo.value);
  };

  const disconnectWebsocket = (reasonCode: SocketCloseReasonCode = 1000) => {
    if (reasonCode === 1000) {
      socketStore.sendMessageDisconnect();
    }
    socketStore.close(reasonCode);
  };

  // 인터넷 연결 대응 로직
  const handleOnline = async () => {
    await connectWebsocket(true);
  };

  // 인터넷 연결 대응 로직
  const handleOffline = async () => {
    toastStore.error(t('internet_disconnected'));
    disconnectWebsocket();
    if (microphoneStore.isMicOn) {
      await microphoneStore.stopMic();
    }
  };

  //  새로고침/탭삭제/URL 이동 대응 로직
  const handleBeforeUnload = async () => {
    if (window.__isDownloading) return;
    disconnectWebsocket(3001);
  };

  const goToEntrance = () => {
    router.push({ name: 'chat-entrance', params: { code: code.value } });
  };

  // 소켓 연결 관리
  watch(
    () => socketStore.socketState,
    async (state) => {
      // 데모 종료 / 퇴장 처리 / 복구 상태일 경우 무시
      if (!code.value || socketStore.endOfDemo || socketStore.evicted || socketStore.recover) {
        return;
      }

      const reconnectAfterDelay = () => {
        setTimeout(async () => {
          if (bottomBarStatus.value === 1) {
            await connectWebsocket(true);
            retryCount.value += 1;
            waitingToRetry.value = false;
          }
        }, 3000);
      };

      const handleRetryOrExit = () => {
        if (!waitingToRetry.value) {
          waitingToRetry.value = true;
          setBottomBarStatus(1);
          reconnectAfterDelay();

          // TODO: 추후 domain !== 'SELF_SERVICE' 삭제
          if (retryCount.value === 0 && state === SocketState.CLOSED && domain !== 'SELF_SERVICE') {
            toastStore.error(t('try_reconnection'), 'RECONNECT');
          }
        }
      };

      switch (state) {
        case SocketState.OPEN: // 소켓 연결 성공
          setTimeout(() => {
            if (socketStore.socketState === SocketState.OPEN) {
              setTimeout(() => {
                socketStore.sendMessageAttendees();
              }, 3000);

              retryCount.value = 0;

              if (bottomBarStatus.value !== -2) {
                setBottomBarStatus(0);
              }
            }
            loading.value = false;
          }, 500);
          break;

        case SocketState.RETRY_FAILED: // 재시도 실패
          setBottomBarStatus(-1);
          socketStore.removeAttendeeAllExcludeMine();
          break;

        case SocketState.CLOSED: // 소켓 닫힘
          if (bottomBarStatus.value !== 2) {
            handleRetryOrExit();
          }
          loading.value = false;
          socketStore.removeAttendeeAllExcludeMine();
          break;

        case SocketState.ERROR_NO_HEADER: // 헤더 없는 에러
          if (socketStore.socket?.readyState !== WebSocket.OPEN) {
            await connectWebsocket(true);
          }
          socketStore.sendMessageAudioStreamStart();
          break;
      }
    }
  );

  watch(
    () => socketStore.connectionResponse,
    async (response) => {
      // 연결 시도 또는 연결 성공
      if (!code.value || [-1, 0, 1].includes(response)) {
        return;
      }

      // 연결 수립 이후 초기화
      if (response === 2) {
        await initProcess();
        return;
      }

      // 그 외 모든 경우는 소켓 종료
      socketStore.close();

      const isEmployee = QRCodeType.isEmployee(callStore.qrCodeInfo.qrCodeType);
      const isPartner = UserType.isPartner(callStore.userInfo.userType);
      const isCallTypeQR = CALL_TYPES_QR.includes(callType.value);

      switch (response) {
        case 1016: // 잘못된 코드 요청
          setBottomBarStatus(2);
          localStorage.setItem('errorCode', String(response));
          router.push({ name: 'chat-ended' });
          break;
        case 1029: // 사용량 초과
          setBottomBarStatus(2);
          alertExceededUsageLimit(() => {
            switch (domain) {
              case 'CHAT':
                if (['AUDIENCE'].includes(callType.value)) {
                  redirectToLoginPage();
                } else {
                  router.push({
                    name: 'chat-entrance',
                    params: { code: code.value },
                  });
                }
                break;
              case 'SELF_SERVICE':
                disconnectWebsocket();
                break;
              default:
                break;
            }
          });

          break;
        case 1030: // 동시 접속시
          setBottomBarStatus(2);
          alertEvictMyself(() => connectWebsocket(true));
          break;

        case 4000:
          if (isCallTypeQR) {
            alertBasic({ title: t('wrong_approach') }, goToEntrance);
          } else {
            alertBasic({ title: t('wrong_approach') }, logoutAndGoToLogin);
          }
          break;

        case 4010: // 인증 실패
          if (isEmployee) {
            alertBasic({ title: t('access_denied_message') }, logoutAndGoToLogin);
          } else {
            await userStore.fetchUserInfo();
            await connectWebsocket(true);
          }
          break;

        case 4030:
          alertBasic({ title: t('entry_limited') }, goToEntrance);
          break;

        case 1026: // 이미 통역 중일 때 or 동시 접속자 초과
          setBottomBarStatus(2);
          localStorage.setItem('errorCode', String(response));
          localStorage.setItem('chatId', socketStore.selectedChatId);
          router.push({ name: 'chat-ended' });
          break;

        case 1027: // 이미 종료된 통역일 때
          setBottomBarStatus(2);
          if (isPartner) {
            alertAlreadyEndedCallWithReconnect(() => connectWebsocket(true));
          } else {
            localStorage.setItem('errorCode', String(response));
            await router.push({ name: 'chat-ended' });
          }
          break;

        case 1023: // 호스트 접속이 안되어있을 때 (FAIR / CONFERENCE)
          setBottomBarStatus(2);
          alertNoEmployee(() =>
            router.push({
              name: 'chat-entrance',
              params: { code: code.value },
            })
          );
          break;
        case 9999: // 호스트 접속이 안되어있을 때 (FAIR / CONFERENCE)
          await connectWebsocket(true);
          break;

        default: // 나머지 에러
          setBottomBarStatus(-1);
          break;
      }
    }
  );

  onMounted(() => {
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('beforeunload', handleBeforeUnload);
  });

  onUnmounted(async () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
    window.removeEventListener('beforeunload', handleBeforeUnload);
  });

  return {
    /* states */
    bottomBarStatus,
    loading,
    /* actions */
    setupWebsocket,
    initChatId,
    setBottomBarStatus,
    connectWebsocket,
    disconnectWebsocket,
    selectedChatId: socketStore.selectedChatId,
  };
};
